<template>
  <app-modal :modal-id="modalId" :title="modalTitle" :preloader="preloader"
             @submit="submit" @close-modal="closeModal" modal-size="extra-large">
    <template slot="header">
      <div class="shift-editor__modal-head">
        <div>
          <h5 class="modal-title mb-0" id="ModalLabel">{{ modalTitle }}</h5>
          <small class="shift-editor__modal-subtitle">{{ $t('please_fill_up_all_fields') }}</small>
        </div>
        <button type="button" class="close outline-none" @click.prevent="closeModal">
          <span>
            <app-icon :name="'x'"></app-icon>
          </span>
        </button>
      </div>
    </template>

    <template slot="body">
      <app-overlay-loader v-if="preloader" />
      <form class="mb-0 shift-editor" :class="{ 'loading-opacity': preloader }" ref="form"
            :data-url='selectedUrl ? `working-shifts/${formData.id}` : `working-shifts`'>
        <div class="shift-editor__layout row no-gutters">
          <div class="col-12 mb-3">
            <section class="shift-editor__panel shift-editor__panel--summary">
              <div class="shift-editor__summary-main">
                <div class="shift-editor__summary-content">
                  <div class="shift-editor__symbol">{{ shiftTypeLabel.charAt(0).toUpperCase() }}</div>
                  <h6 class="shift-editor__summary-title">{{ shiftDisplayName }}</h6>
                  <p class="shift-editor__summary-meta">{{ selectedLocationName }}</p>
                  <div class="shift-editor__chips">
                    <span class="shift-editor__chip">{{ shiftTypeLabel }}</span>
                    <span class="shift-editor__chip shift-editor__chip--muted">{{ activeDaysCount }}/7</span>
                  </div>
                  <div class="shift-editor__meta-card">
                    <small>{{ $fieldTitle('working_shift_type', '') }}</small>
                    <strong>{{ shiftTypeLabel }}</strong>
                  </div>
                </div>
                <div class="shift-editor__summary-visual" aria-hidden="true">
                  <img
                    src="/images/shift-banner.svg"
                    alt=""
                    class="shift-editor__summary-illustration"
                  />
                </div>
              </div>
            </section>
          </div>

          <div class="col-12">
            <section class="shift-editor__panel shift-editor__panel--section">
              <div class="shift-editor__section-head">
                <h6>{{ $t('working_shift') }}</h6>
                <p>{{ $t('name') }} / {{ $t('location') }} / {{ $t('start_date') }}</p>
              </div>

              <app-form-group
                :label="$t('name')"
                type="text"
                v-model="formData.name"
                :placeholder="$t('name')"
                :required="true"
                :error-message="$errorMessage(errors, 'name')"
              />

              <div class="form-group">
                <label for="working_shift_location" class="shift-editor__label">{{ $t('location') }}</label>
                <app-input class="w-100"
                           id="working_shift_location"
                           v-model="formData.location_id"
                           :list="locationList"
                           list-value-field="name"
                           :placeholder="$t('choose_one')"
                           type="search-select"
                           :required="true" />
              </div>

              <div class="row">
                <div class="col-md-6">
                  <app-form-group
                    :disabled="!!formData.is_default"
                    :label="$t('start_date')"
                    type="date"
                    v-model="formData.start_date"
                    :placeholder="$t('start_date')"
                    :required="true"
                    :error-message="$errorMessage(errors, 'start_date')"
                  />
                </div>
                <div class="col-md-6">
                  <app-form-group
                    :disabled="!!formData.is_default"
                    :label="$t('end_date')"
                    type="date"
                    v-model="formData.end_date"
                    :placeholder="$t('end_date')"
                    :error-message="$errorMessage(errors, 'end_date')"
                  />
                </div>
              </div>
            </section>

            <section class="shift-editor__panel shift-editor__panel--section mt-3">
              <div class="shift-editor__section-head">
                <h6>{{ $fieldTitle('working_shift_type', '') }}</h6>
                <p>{{ checkDisable ? $fieldTitle('working_shift_type', '') : $fieldTitle('choose_a_working_shift_type', '') }}</p>
              </div>

              <div class="form-group shift-editor__radio-wrap">
                <app-input
                  type="radio"
                  :list="[
                    {id: 'regular', value: $t('regular')},
                    {id: 'scheduled', value: $t('scheduled')}
                  ]"
                  v-model="formData.type"
                  :error-message="$errorMessage(errors, 'type')"
                />
              </div>

              <div v-if="formData.type === 'regular'" class="shift-editor__config">
                <div class="shift-editor__config-head">
                  <label v-if="checkDisable">{{ $fieldTitle('regular_week', '') }}</label>
                  <label v-else>{{ $fieldTitle('set_regular_week', '') }}</label>
                  <small class="text-muted">
                    ({{ $fieldTitle('set_week_with_fixed_time', '') }})
                  </small>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="shift-editor__label">{{ $fieldTitle('start_time', '') }}</label>
                      <app-input
                        :id="'start_at'"
                        type="time"
                        v-model="formData.start_at"
                        :placeholder="$t('start_time')"
                        name="start_time"
                        :error-message="$errorMessage(errors, 'start_at')"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="shift-editor__label">{{ $fieldTitle('end_time', '') }}</label>
                      <app-input
                        :id="'end-at'"
                        type="time"
                        v-model="formData.end_at"
                        :placeholder="$t('end_time')"
                        :error-message="$errorMessage(errors, 'end_at')"
                      />
                    </div>
                  </div>
                </div>

                <div v-if="formData.start_at && formData.end_at" class="row mb-2">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="shift-editor__label">{{ $t('break_minutes') }}</label>
                      <app-input
                        type="number"
                        min="0"
                        max="480"
                        v-model="formData.break_minutes"
                        :placeholder="$t('break_minutes')"
                        :error-message="$errorMessage(errors, 'break_minutes')"
                      />
                    </div>
                  </div>
                  <div class="col-md-6"></div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="shift-editor__label">{{ $t('grace_time_before') }}</label>
                      <app-input
                        type="number"
                        min="0"
                        max="60"
                        v-model="formData.grace_time_before"
                        :placeholder="$t('grace_time_before')"
                        :error-message="$errorMessage(errors, 'grace_time_before')"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="shift-editor__label">{{ $t('grace_time_after') }}</label>
                      <app-input
                        type="number"
                        min="0"
                        max="60"
                        v-model="formData.grace_time_after"
                        :placeholder="$t('grace_time_after')"
                        :error-message="$errorMessage(errors, 'grace_time_after')"
                      />
                    </div>
                  </div>
                </div>

                <div class="my-3">
                  <h6 v-if="checkDisable" class="shift-editor__weekend-title">{{ $fieldTitle('weekend_days', '') }}</h6>
                  <h6 v-else class="shift-editor__weekend-title">{{ $fieldTitle('select_your_weekend_days', '') }}</h6>
                </div>
                <div class="mb-primary">
                  <app-input
                    type="checkbox"
                    :list="weekdays"
                    v-model="formData.weekdays"
                    radio-checkbox-wrapper="row"
                    custom-checkbox-type="checkbox-default col-md-4 col-lg-3"
                    :error-message="$errorMessage(errors, 'weekdays')"
                  />
                </div>
              </div>

              <div v-else class="shift-editor__config">
                <div class="shift-editor__config-head">
                  <label v-if="checkDisable">{{ $fieldTitle('scheduled_week', '') }}</label>
                  <label v-else>{{ $fieldTitle('set_scheduled_week', '') }}</label>
                  <small class="text-muted">
                    ({{
                      $fieldTitle('set_week_with_customized_time_and_without_time_and_without_time_it_will_be_weekend', '')
                    }})
                  </small>
                </div>

                <div class="row mt-3">
                  <div v-for="(item, index) in formData.details" :key="item.weekday" class="col-md-6 shift-editor__day-col">
                    <div class="shift-editor__day-card">
                      <div class="shift-editor__day-head">
                        <label class="shift-editor__day-label mb-0">
                          {{ $t(item.weekday) }}
                        </label>
                        <div class="shift-editor__day-actions">
                          <button
                            type="button"
                            class="btn btn-sm"
                            :class="item.is_weekend ? 'btn-outline-secondary' : 'btn-success'"
                            @click="toggleScheduledDay(index, true)"
                          >
                            {{ $t('working_day') }}
                          </button>
                          <button
                            type="button"
                            class="btn btn-sm"
                            :class="item.is_weekend ? 'btn-outline-danger' : 'btn-outline-secondary'"
                            @click="toggleScheduledDay(index, false)"
                          >
                            {{ $t('day_off') }}
                          </button>
                        </div>
                      </div>
                      <p class="shift-editor__day-caption mb-3 text-muted">
                        {{ item.is_weekend ? $t('day_off') : $t('working_day') }}
                      </p>

                      <div class="form-row">
                        <div class="col-md-6">
                          <div class="form-group">
                            <app-input
                              :id="'start-at-'+index"
                              type="time"
                              v-model="item.start_at"
                              :disabled="!!item.is_weekend"
                              :placeholder="$t('start_time')"
                              :error-message="errorMessageForArray(
                                errors,
                                `details.${index}.start_at`,
                                `details.${index}.end at`
                              )"
                            />
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="form-group">
                            <app-input
                              :id="'end-at-'+index"
                              type="time"
                              v-model="item.end_at"
                              :disabled="!!item.is_weekend"
                              :placeholder="$t('end_time')"
                              :error-message="errorMessageForArray(
                                errors,
                                `details.${index}.end_at`,
                                `details.${index}.start at`
                              )"
                            />
                          </div>
                        </div>
                      </div>

                      <div v-if="!item.is_weekend" class="form-row mt-1">
                        <div class="col-md-4">
                          <div class="form-group">
                            <label class="small text-muted">{{ $t('break_minutes') }}</label>
                            <app-input
                              :id="'break-minutes-'+index"
                              type="number"
                              min="0"
                              max="480"
                              v-model="item.break_minutes"
                              :disabled="!!item.is_weekend"
                              :placeholder="$t('break_minutes')"
                            />
                          </div>
                        </div>
                        <div class="col-md-4">
                          <div class="form-group">
                            <label class="small text-muted">{{ $t('grace_time_before') }}</label>
                            <app-input
                              :id="'grace-before-'+index"
                              type="number"
                              min="0"
                              max="60"
                              v-model="item.grace_time_before"
                              :disabled="!!item.is_weekend"
                              :placeholder="$t('grace_time_before')"
                            />
                          </div>
                        </div>
                        <div class="col-md-4">
                          <div class="form-group">
                            <label class="small text-muted">{{ $t('grace_time_after') }}</label>
                            <app-input
                              :id="'grace-after-'+index"
                              type="number"
                              min="0"
                              max="60"
                              v-model="item.grace_time_after"
                              :disabled="!!item.is_weekend"
                              :placeholder="$t('grace_time_after')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </form>
    </template>

    <template slot="footer">
      <div class="shift-editor__footer">
        <small class="text-muted">* {{ $t('required') }}</small>
        <div class="shift-editor__actions">
          <button type="button" class="btn btn-secondary mr-2" data-dismiss="modal" @click="closeModal">
            {{ $t('cancel') }}
          </button>
          <button type="button" class="btn btn-primary" @click="submit">{{ $t('save') }}</button>
        </div>
      </div>
    </template>
  </app-modal>
</template>

<script>
import { ModalMixin } from '../../../Mixins/ModalMixin';
import {errorMessageForArray} from '../../../../common/Helper/Support/FormHelper';
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import {formatDateForServer, formatUtcToLocal} from "../../../../common/Helper/Support/DateTimeHelper";
import {cloneDeep} from 'lodash';
import {mapState} from 'vuex';
import moment from 'moment';

export default {
    name: 'WorkingShiftAddEditModal',
    mixins: [FormHelperMixins, ModalMixin],
    props: {
        tableId: {
            type: String,
        },
    },
    data() {
        return {
            preloader: false,
            errorMessageForArray,
            modalId: 'working-shift-add-edit-modal',
            weekdays: [
                {id: "mon", value: this.$t("monday")},
                {id: "tue", value: this.$t("tuesday")},
                {id: "wed", value: this.$t("wednesday")},
                {id: "thu", value: this.$t("thursday")},
                {id: "fri", value: this.$t("friday")},
                {id: "sat", value: this.$t("saturday")},
                {id: "sun", value: this.$t("sunday")},
            ],
            formData: {
                name: '',
                type: 'regular',
                location_id: null,
                weekdays: [],
                attendances_count: 0,
                break_minutes: 0,
                grace_time_before: 0,
                grace_time_after: 0,
                details: [
                    {
                        weekday: 'mon',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'tue',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'wed',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'thu',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'fri',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'sat',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 1
                    },
                    {
                        weekday: 'sun',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 1
                    },
                ],
            },
            isModalActive: false,
            modalTitle: `${this.$t('new')} ${this.$t('working-shift')}`,
        }
    },
    computed:{
        checkDisable () {
            return this.selectedUrl ? (this.formData.attendances_count > 0 || !this.$can('update_working_shifts') || this.viewOnly ):
              !this.$can('create_working_shifts')
        },
        ...mapState({
            locationList: state => state.selects.locationList,
        }),
        shiftDisplayName() {
            return this.formData.name || `${this.$t('new')} ${this.$t('working-shift')}`;
        },
        selectedLocationName() {
            if (!this.formData.location_id || !Array.isArray(this.locationList)) {
                return this.$t('location');
            }

            const location = this.locationList.find(item => `${item.id}` === `${this.formData.location_id}`);
            return location ? location.name : this.$t('location');
        },
        shiftTypeLabel() {
            return this.formData.type === 'scheduled' ? this.$t('scheduled') : this.$t('regular');
        },
        regularWorkingDaysCount() {
            const weekendCount = Array.isArray(this.formData.weekdays) ? this.formData.weekdays.length : 0;
            return Math.max(0, 7 - weekendCount);
        },
        scheduledWorkingDaysCount() {
            if (!Array.isArray(this.formData.details)) {
                return 0;
            }

            return this.formData.details.filter(day => !day.is_weekend).length;
        },
        activeDaysCount() {
            return this.formData.type === 'scheduled' ? this.scheduledWorkingDaysCount : this.regularWorkingDaysCount;
        }
    },
    methods: {
        submit() {
            this.loading = true;
            this.message = '';
            this.errors = {};

            let formData = {};
            if (this.formData.type === 'regular') {
                formData = this.prepareRegularType(this.formData);
            } else {
                formData = this.prepareSchedulerType(this.formData);
            }

            formData.start_date = formatDateForServer(formData.start_date);
            formData.end_date = formatDateForServer(formData.end_date);

            this.save(formData);
        },
        afterSuccess({data}) {
            this.formData = {};
            $('#working-shift-add-edit-modal').modal('hide')
            this.toastAndReload(data.message, 'working-shifts-table');
        },
        afterSuccessFromGetEditData({data}) {
            this.formData = data;
            this.formData.start_date = data.start_date ? new Date(data.start_date) : '';
            this.formData.end_date = data.end_date ? new Date(data.end_date) : '';
            this.formData.start_at = data.start_at ? moment(data.start_at, 'HH:mm:ss').format('HH:mm') : '';
            this.formData.end_at = data.end_at ? moment(data.end_at, 'HH:mm:ss').format('HH:mm') : '';

            const regularDayDetail = Array.isArray(data.details)
                ? data.details.find(day => !parseInt(day.is_weekend, 10))
                : null;
            const fallbackBreakMinutes = regularDayDetail ? regularDayDetail.break_minutes : null;
            const fallbackGraceBefore = regularDayDetail ? regularDayDetail.grace_time_before : null;
            const fallbackGraceAfter = regularDayDetail ? regularDayDetail.grace_time_after : null;

            // Charger les champs soit depuis le top-level (si fourni), soit depuis un jour ouvré
            this.formData.break_minutes = data.break_minutes ?? fallbackBreakMinutes ?? 0;
            this.formData.grace_time_before = data.grace_time_before ?? fallbackGraceBefore ?? 0;
            this.formData.grace_time_after = data.grace_time_after ?? fallbackGraceAfter ?? 0;
            
            this.formData.weekdays = data.details.filter(week => {
                return parseInt(week.is_weekend);
            }).map(week => {
                return week.weekday;
            });

            this.formData.details = data.details.map(details => {
                return {
                    ...details,
                    start_at: details.start_at ? moment(details.start_at, 'HH:mm:ss').format('HH:mm') : '',
                    end_at: details.end_at ? moment(details.end_at, 'HH:mm:ss').format('HH:mm') : '',
                    break_minutes: details.break_minutes ?? 0,
                    grace_time_before: details.grace_time_before ?? 0,
                    grace_time_after: details.grace_time_after ?? 0,
                    is_weekend: parseInt(details.is_weekend, 10) ? 1 : 0,
                }
            });

            this.preloader = false;
        },
        prepareRegularType(formData) {
            let data = cloneDeep(formData);

            if (!(data.start_at && data.end_at)) {
                return data;
            }

            data.details.map(day => {
                if (data.weekdays.includes(day.weekday)) {
                    day.is_weekend = 1;
                    return day;
                }

                day.end_at = data.end_at ? moment(data.end_at, 'HH:mm').format('HH:mm') : '';
                day.start_at = data.start_at ? moment(data.start_at, 'HH:mm').format('HH:mm') : '';
                day.break_minutes = data.break_minutes ?? 0;
                day.grace_time_before = data.grace_time_before ?? 0;
                day.grace_time_after = data.grace_time_after ?? 0;
                day.is_weekend = data.weekdays.includes(day.weekday) ? 1 : 0
                return day;
            });

            return data;
        },
        prepareSchedulerType(formData) {
            let data = cloneDeep(formData);
            data.details.map(day => {
                day.is_weekend = day.is_weekend ? 1 : 0;

                if (day.is_weekend) {
                    day.start_at = '';
                    day.end_at = '';
                    day.break_minutes = 0;
                    day.grace_time_before = 0;
                    day.grace_time_after = 0;
                    return day;
                }

                day.start_at = day.start_at ? moment(day.start_at, 'HH:mm').format('HH:mm') : '';
                day.end_at = day.end_at ? moment(day.end_at, 'HH:mm').format('HH:mm') : '';
                day.break_minutes = day.break_minutes ?? 0;
                day.grace_time_before = day.grace_time_before ?? 0;
                day.grace_time_after = day.grace_time_after ?? 0;
                return day;
            })
            return data;
        },
        toggleScheduledDay(index, isWorking) {
            const day = this.formData.details[index];

            if (!day) {
                return;
            }

            day.is_weekend = isWorking ? 0 : 1;

            if (day.is_weekend) {
                day.start_at = '';
                day.end_at = '';
                day.break_minutes = 0;
                day.grace_time_before = 0;
                day.grace_time_after = 0;
                return;
            }

            day.start_at = day.start_at || '09:00';
            day.end_at = day.end_at || '17:00';
            day.break_minutes = day.break_minutes ?? 0;
            day.grace_time_before = day.grace_time_before ?? 0;
            day.grace_time_after = day.grace_time_after ?? 0;
        }
    },
    mounted() {
        this.$store.dispatch('getLocations');
    },
}
</script>

<style scoped>
.shift-editor {
  max-height: none;
  overflow-y: visible;
  padding-right: 0;
}

.shift-editor__layout {
  align-items: flex-start;
}

.shift-editor__modal-head {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.shift-editor__modal-subtitle {
  display: inline-block;
  margin-top: 0.2rem;
  color: var(--sidebar-text-muted);
  font-size: 0.78rem;
  letter-spacing: 0.01em;
}

.shift-editor__panel {
  border: 1px solid var(--default-border-color);
  border-radius: 16px;
  background: var(--default-card-bg);
}

.shift-editor__panel--summary {
  padding: 1.35rem;
  background-image: linear-gradient(145deg, rgba(59, 130, 246, 0.08), transparent 70%);
}

.shift-editor__summary-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.2rem;
}

.shift-editor__summary-content {
  flex: 1 1 auto;
  min-width: 0;
}

.shift-editor__summary-visual {
  flex: 0 0 auto;
  width: 180px;
  max-width: 34%;
  align-self: flex-end;
}

.shift-editor__summary-illustration {
  display: block;
  width: 100%;
  height: auto;
}

.shift-editor__panel--section {
  padding: 1.05rem 1.15rem 0.2rem;
}

.shift-editor__section-head {
  margin-bottom: 0.9rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--default-border-color);
}

.shift-editor__section-head h6 {
  margin-bottom: 0.15rem;
  font-size: 0.93rem;
  font-weight: 600;
  color: var(--default-font-color);
}

.shift-editor__section-head p {
  margin-bottom: 0;
  color: var(--sidebar-text-muted);
  font-size: 0.78rem;
}

.shift-editor__symbol {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.8rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1e3a8a;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(6, 182, 212, 0.18));
}

.shift-editor__summary-title {
  margin-bottom: 0.25rem;
  color: var(--default-font-color);
  font-size: 1rem;
  font-weight: 600;
  word-break: break-word;
}

.shift-editor__summary-meta {
  margin-bottom: 0.65rem;
  color: var(--sidebar-text-muted);
  font-size: 0.82rem;
  word-break: break-word;
}

.shift-editor__chips {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.shift-editor__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.2rem 0.62rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: capitalize;
  background: rgba(59, 130, 246, 0.14);
  color: #1e3a8a;
}

.shift-editor__chip--muted {
  background: rgba(148, 163, 184, 0.2);
  color: #334155;
}

.shift-editor__meta-card {
  margin-top: 0.95rem;
  padding-top: 0.75rem;
  border-top: 1px dashed rgba(148, 163, 184, 0.45);
  display: flex;
  flex-direction: column;
}

.shift-editor__meta-card small {
  color: var(--sidebar-text-muted);
  font-size: 0.73rem;
}

.shift-editor__meta-card strong {
  color: var(--default-font-color);
  text-transform: capitalize;
  font-size: 0.86rem;
}

.shift-editor__label {
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--default-font-color);
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.shift-editor__radio-wrap {
  margin-bottom: 0.65rem;
}

.shift-editor__config-head {
  margin: 0.35rem 0 0.7rem;
}

.shift-editor__config-head label {
  margin-bottom: 0;
  font-weight: 600;
  color: var(--default-font-color);
}

.shift-editor__weekend-title {
  color: #dc2626;
  font-size: 0.89rem;
  font-weight: 600;
  margin-bottom: 0;
}

.shift-editor__day-col {
  margin-bottom: 0.9rem;
}

.shift-editor__day-card {
  border: 1px solid var(--default-border-color);
  border-radius: 12px;
  padding: 0.75rem 0.75rem 0.2rem;
  background: rgba(148, 163, 184, 0.06);
}

.shift-editor__day-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.shift-editor__day-label {
  font-size: 0.81rem;
  font-weight: 600;
  color: var(--default-font-color);
  margin-bottom: 0.5rem;
}

.shift-editor__day-caption {
  font-size: 0.78rem;
}

.shift-editor__day-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.shift-editor__day-actions .btn {
  min-width: 104px;
  border-radius: 999px;
}

.shift-editor__footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.shift-editor__actions {
  display: inline-flex;
  align-items: center;
}

@media (max-width: 767.98px) {
  .shift-editor__summary-main {
    flex-direction: column;
  }

  .shift-editor__summary-visual {
    width: 100%;
    max-width: 240px;
    align-self: flex-end;
  }
}

.shift-editor ::v-deep .form-group {
  margin-bottom: 0.92rem;
}

.shift-editor ::v-deep input,
.shift-editor ::v-deep select,
.shift-editor ::v-deep textarea,
.shift-editor ::v-deep .dropdown-toggle,
.shift-editor ::v-deep .vs__dropdown-toggle {
  border-radius: 10px !important;
  border-color: var(--default-border-color) !important;
  min-height: 42px;
}

.shift-editor ::v-deep input:focus,
.shift-editor ::v-deep select:focus,
.shift-editor ::v-deep textarea:focus,
.shift-editor ::v-deep .dropdown-toggle:focus,
.shift-editor ::v-deep .vs__dropdown-toggle:focus {
  border-color: rgba(59, 130, 246, 0.72) !important;
}

@media (max-width: 1199.98px) {
  .shift-editor__panel--summary {
    padding: 1rem;
  }
}

@media (max-width: 767.98px) {
  .shift-editor__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .shift-editor__actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
